import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { X } from "lucide-react";

import Button from "../../../../components/buttons/Button";
import TextField from "../../../../components/input/textfield/TextField";
import Select from "../../../../components/input/select/Select";

import { useCreatorPops } from "../hooks/useCreatorPops";

import "./CreatorPopEditForm.css";

const VISIBILITY_OPTIONS = [
  { label: "Public", value: "public" },
  { label: "Unlisted", value: "unlisted" },
  { label: "Private", value: "private" },
];

const VALID_VISIBILITY = new Set(
  VISIBILITY_OPTIONS.map((option) => option.value),
);

const getVisibilityNote = (visibility) => {
  switch (visibility) {
    case "public":
      return "This Pop is available publicly.";

    case "unlisted":
      return "Only people with access to this Pop can view it.";

    default:
      return "Only you can access this Pop.";
  }
};

const getErrorMessage = (error, fallback) =>
  error?.response?.data?.message || error?.message || fallback;

export default function CreatorPopEditForm({
  popTag,
  username,
  onClose,
  onCancel,
  onSuccess,
}) {
  const {
    loadingEdit,
    error,
    saving,
    getCreatorPopDetails,
    updateCreatorPopDetails,
  } = useCreatorPops(username);

  const [popDetails, setPopDetails] = useState(null);
  const [initializing, setInitializing] = useState(true);
  const [submitError, setSubmitError] = useState("");
  const [localSaving, setLocalSaving] = useState(false);

  const savingRef = useRef(false);

  const loading = initializing || loadingEdit || saving || localSaving;

  const visibility = String(popDetails?.visibility || "private")
    .trim()
    .toLowerCase();

  const errorMessage =
    submitError || (typeof error === "string" ? error : error?.message || "");

  const canSubmit =
    Boolean(popTag) &&
    Boolean(username) &&
    Boolean(popDetails) &&
    Boolean(String(popDetails?.title || "").trim()) &&
    VALID_VISIBILITY.has(visibility) &&
    !loading;

  useEffect(() => {
    let active = true;

    const loadPop = async () => {
      setPopDetails(null);
      setSubmitError("");
      setInitializing(true);

      if (!popTag || !username) {
        setSubmitError("Pop information is missing.");
        setInitializing(false);
        return;
      }

      try {
        const data = await getCreatorPopDetails(popTag, username);

        if (!active) return;

        if (!data) {
          throw new Error("Unable to load Pop details.");
        }

        const response = data?.data ?? data;

        if (response?.success === false) {
          throw new Error(response.message || "Unable to load Pop details.");
        }

        const pop = response?.pop;

        if (!pop || typeof pop !== "object") {
          throw new Error("Pop details are unavailable.");
        }

        setPopDetails({
          ...pop,
          title: String(pop.title || ""),
          tags: String(pop.tags || ""),
          visibility: String(pop.visibility || "private")
            .trim()
            .toLowerCase(),
        });
      } catch (loadError) {
        if (active) {
          console.error("Unable to load creator Pop edit details:", loadError);

          setSubmitError(
            getErrorMessage(loadError, "Unable to load Pop details."),
          );
        }
      } finally {
        if (active) {
          setInitializing(false);
        }
      }
    };

    void loadPop();

    return () => {
      active = false;
    };
  }, [popTag, username]);

  const closeForm = () => {
    if (typeof onClose === "function") {
      onClose();
    } else {
      onCancel?.(false);
    }
  };

  const handleClose = () => {
    if (loading || savingRef.current) return;
    closeForm();
  };

  useEffect(() => {
    const handleEscape = (event) => {
      if (event.key === "Escape" && !loading && !savingRef.current) {
        event.preventDefault();

        if (typeof onClose === "function") {
          onClose();
        } else {
          onCancel?.(false);
        }
      }
    };

    document.addEventListener("keydown", handleEscape);

    return () => {
      document.removeEventListener("keydown", handleEscape);
    };
  }, [loading, onClose, onCancel]);

  const handleInputChange = (event) => {
    const { name, value } = event.target;

    setPopDetails((previous) => ({
      ...previous,
      [name]: value,
    }));

    setSubmitError("");
  };

  const handleSubmit = async (event) => {
    event?.preventDefault?.();

    if (loading || savingRef.current || !popDetails) return;

    setSubmitError("");

    const title = String(popDetails.title || "").trim();
    const tags = String(popDetails.tags || "").trim();

    if (!title) {
      setSubmitError("Pop title is required.");
      return;
    }

    if (title.length > 255) {
      setSubmitError("Pop title cannot exceed 255 characters.");
      return;
    }

    if (!VALID_VISIBILITY.has(visibility)) {
      setSubmitError("Select Public, Unlisted or Private visibility.");
      return;
    }

    const payload = {
      title,
      tags,
      visibility,
    };

    savingRef.current = true;
    setLocalSaving(true);

    try {
      const result = await updateCreatorPopDetails(popTag, payload);

      if (result === false || result?.success === false) {
        throw new Error(result?.message || "Unable to update Pop details.");
      }

      await onSuccess?.(result);
      closeForm();
    } catch (saveError) {
      console.error("Unable to save creator Pop:", saveError);

      setSubmitError(getErrorMessage(saveError, "Unable to save Pop changes."));
    } finally {
      savingRef.current = false;
      setLocalSaving(false);
    }
  };

  return createPortal(
    <form
      className="creator-pop-edit-form"
      onSubmit={handleSubmit}
      aria-label="Edit Pop"
    >
      <header className="creator-pop-edit-form__header">
        <div className="creator-pop-edit-form__header-info">
          <h2>Edit Pop</h2>
          <p>Update your Pop information and settings.</p>
        </div>

        <button
          type="button"
          className="creator-pop-edit-form__close"
          onClick={handleClose}
          disabled={loading}
          aria-label="Close Pop editor"
        >
          <X size={20} />
        </button>
      </header>

      <div className="creator-pop-edit-form__workspace">
        <main className="creator-pop-edit-form__main">
          <div className="creator-pop-edit-form__section-header">
            <h3>Pop Information</h3>
            <p>Update the details displayed to your viewers.</p>
          </div>

          {popDetails ? (
            <div className="creator-pop-edit-form__fields">
              <TextField
                label="Title"
                name="title"
                value={popDetails.title}
                onChange={handleInputChange}
                maxLength={255}
                required
                disabled={loading}
                placeholder="Enter Pop title"
              />

              <TextField
                label="Tags"
                name="tags"
                value={popDetails.tags}
                onChange={handleInputChange}
                disabled={loading}
                placeholder="Enter tags"
              />
            </div>
          ) : (
            <div className="creator-pop-edit-form__placeholder" role="status">
              {initializing || loadingEdit
                ? "Loading Pop details..."
                : "Pop details are unavailable."}
            </div>
          )}
        </main>

        <aside className="creator-pop-edit-form__sidebar">
          <div className="creator-pop-edit-form__section-header">
            <h3>Pop Settings</h3>
            <p>Manage your Pop's visibility.</p>
          </div>

          <div className="creator-pop-edit-form__fields">
            <Select
              label="Visibility"
              name="visibility"
              value={visibility}
              onChange={handleInputChange}
              items={VISIBILITY_OPTIONS}
              required
              disabled={loading || !popDetails}
            />

            <p className="creator-pop-edit-form__visibility-note">
              {getVisibilityNote(visibility)}
            </p>
          </div>

          {popDetails && (
            <div className="creator-pop-edit-form__summary">
              <h4>Pop Summary</h4>

              <div>
                <span>Title</span>
                <strong>{popDetails.title || "—"}</strong>
              </div>

              <div>
                <span>Visibility</span>
                <strong>{visibility}</strong>
              </div>
            </div>
          )}
        </aside>
      </div>

      <footer className="creator-pop-edit-form__footer">
        <div className="creator-pop-edit-form__footer-status">
          {errorMessage ? (
            <span className="creator-pop-edit-form__error" role="alert">
              {errorMessage}
            </span>
          ) : initializing || loadingEdit ? (
            <span>Loading Pop details...</span>
          ) : saving || localSaving ? (
            <span>Saving changes...</span>
          ) : null}
        </div>

        <div className="creator-pop-edit-form__actions">
          <Button
            type="button"
            value="Cancel"
            variant="primary-outlined"
            onClick={handleClose}
            disabled={loading}
          />

          <Button
            type="submit"
            value={saving || localSaving ? "Saving..." : "Save Changes"}
            variant="primary"
            onClick={handleSubmit}
            disabled={!canSubmit}
          />
        </div>
      </footer>
    </form>,
    document.body,
  );
}
